/* @flow */
import React from 'react';

import Layout from '../../components/Layout';
import NotFound from '../not-found/NotFound';

import { checkStatus, getCityConfig, parseJSON } from '../../apiUtils';
import { addJavaHeaders, checkIsResaleById, getCity, getCityIdsGetArray } from '../../utils';

import {
	getApartAdvert,
	getApartInfrastructure,
	getApartOnMap,
	getApartSelectionInfo,
	setApartment,
	setApartmentFail,
	setApartmentStart,
	setUserStatus,
} from '../../actions/apartment';
import { setCity } from '../../actions/city';
import { logger } from '../../utils/misc';
import type { TSeoData } from '../../types/core';
import Apart from '../../components/ApartPage';
import ApartResale from '../../components/ApartResalePage';
import type { TLiraFavorite } from '../../types/lira3';
import { LoaderWrapper } from '../../components/ApartPage/styles';
import Loader from '../../components/Loader';
import { getTextsFromDatabase } from '../../actions/lira';
import { LIRA_SET_LIVING_BANNER_TEXTS } from '../../constants';
import { SET_MORTGAGE_PARAMS } from '../../constants/mortgage';

async function action({ fetch, params, store }) {
	const {
		token: { token },
		cities,
		cityConfig,
		apartment,
		liraSelections,
		filterGlobal,
	} = store.getState();

	const city = setCity({
		store,
		params,
		fetch,
	});

	const checkedCity = getCity(params.city, cities);

	if (!checkedCity) {
		return {
			chunks: ['not-found'],
			title: 'Страница не найдена',
			component: (
				<Layout isNotFound>
					<NotFound title={this.title} />
				</Layout>
			),
			status: 404,
		};
	}

	if (!cityConfig) {
		await getCityConfig(city.cityIds, {
			store,
			fetch,
		});
	}

	let seoData: TSeoData;

	const apartId = params.id;

	try {
		const seoRes = await fetch(`/lexsApi/seo?${getCityIdsGetArray(city.cityIds)}&page=apartment&id=${params.id}`);
		seoData = await seoRes.json();
	} catch (e) {
		logger(e);
	}

	const title = seoData?.metaTitle || null;
	const description = seoData?.metaDescription || null;
	const keywords = seoData?.metaKeywords || null;

	const { apartData, apartDataResale, isLoaded, isLoading } = apartment;

	const isResale = checkIsResaleById(apartId);
	const apartStore = isResale ? apartDataResale : apartData;
	let buildingId;
	const isMortgage = filterGlobal?.selectionMode === 'mortgage';

	const getType = () => {
		if (liraSelections.isLoaded && !liraSelections.isLoading) {
			if (liraSelections?.data?.liked.some((item: TLiraFavorite) => item.id === apartId)) {
				return 'LIKE';
			}
			if (liraSelections?.data?.disliked.some((item: TLiraFavorite) => item.id === apartId)) {
				return 'DISLIKE';
			}
		}
		return null;
	};

	store.dispatch(setUserStatus(getType()));

	if (!apartStore || apartStore.id !== apartId) {
		const apartURL = '/lexsApi/apartment/extended-card';
		const fetchOptions = {
			method: 'POST',
			...addJavaHeaders(token),
			body: JSON.stringify({
				cityIds: city.cityIds,
				id: apartId,
			}),
		};

		store.dispatch(setApartmentStart(isResale));

		try {
			await fetch(apartURL, fetchOptions)
				.then(checkStatus)
				.then(parseJSON)
				.then((results) => {
					const { data = null } = results;
					buildingId = isResale ? data?.house?.id : data?.complex?.id;
					store.dispatch(setApartment(data, isResale));
				});
		} catch (error) {
			store.dispatch(setApartmentFail());

			return {
				chunks: ['not-found'],
				title: 'Страница не найдена',
				component: (
					<Layout isNotFound>
						<NotFound />
					</Layout>
				),
				status: 404,
			};
		}
	}

	await Promise.all([
		getApartOnMap(apartId, city.cityIds, isResale)(store.dispatch, store.getState, { fetch }),
		getApartSelectionInfo(apartId, city.cityIds, isResale)(store.dispatch, store.getState, {
			fetch,
		}),
	]);

	if (isResale) {
		await getApartAdvert(apartId)(store.dispatch, store.getState, { fetch });
	}

	if (buildingId) {
		await getApartInfrastructure(buildingId, city.cityIds, isResale)(store.dispatch, store.getState, {
			fetch,
		});
	}

	if (!apartment.livingBanner) {
		await getTextsFromDatabase('site_self_cta_block', ['block_text'], LIRA_SET_LIVING_BANNER_TEXTS)(
			store.dispatch,
			store.getState,
			{ fetch },
		);
	}

	if (!isMortgage && apartStore?.priceTotalMin) {
		const initialFee = Math.round((apartStore.priceTotalMin * 0.3) / 100000) * 100000;
		const monthlyPayment = apartStore?.mortgageInfo?.monthlyPayment
			? Math.trunc(Number((apartStore.mortgageInfo.monthlyPayment / 100000).toFixed(1)) * 100000)
			: Math.round(initialFee / 1000000) * 30000;
		store.dispatch({ type: SET_MORTGAGE_PARAMS, payload: { initialFee, monthlyPayment } });
	}

	const currentComponent = isResale ? <ApartResale /> : <Apart />;

	return {
		chunks: ['apart'],
		title,
		description,
		keywords,
		component: (
			<Layout route="apart" menuWithContext isApartResale={isResale}>
				{isLoading && !isLoaded ? (
					<LoaderWrapper>
						<Loader />
					</LoaderWrapper>
				) : (
					currentComponent
				)}
			</Layout>
		),
	};
}

export default action;
